// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";

// component
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import Modal from "components/molecules/ModalContainer/ModalContainer";

// redux
import {
	getStoreList,
	editUser as editUserAPI,
	getUserDetails,
	selectedUserDetailsSelector,
	usersDataLoadingSelector,
	deactivateUser as deactivateUserAPI,
	activateUser as activateUserAPI,
	resendTemporaryPassword as resendTemporaryPasswordAPI,
	getStoreListSelector,
	resetSelectedAdminDetails,
	resetActivateUser,
	resetDeactivateUser,
	resetEditUser,
	resetResendTemporaryPassword,
	getUserPermissionListSelector,
	getStoreListLoading
} from "redux/userList/userSlice";

// hook
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// constants
import { Routes, USER_STATE, AccountManagement as Constants, ViewMode } from "constants/global";

// services & utils
import type { OutletItem } from "services/api/types";
import { validateEmail, validateUsername } from "utils/Validations/fieldValidations";

type AdminData = {
	username: string;
	email: string;
};

export type CreateUserPayload = {
	username: string;
	email: string;
	groups: Array<string>;
	tenant_code?: string;
};

export type LocationState = {
	state: { username: string };
	pathname: string;
};

type FieldErrors = {
	username?: boolean;
	email?: boolean;
};

export function useViewEditAccountPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();
	const { state, pathname } = location as LocationState;

	const userDetails = useAppSelector(selectedUserDetailsSelector);
	const { data, errors } = userDetails || {};

	const storeData = useAppSelector(getStoreListSelector);
	const { data: storeConfigData } = storeData || {};

	const loadingUsersData = useAppSelector(usersDataLoadingSelector) === 1;
	const storeListLoading = useAppSelector(getStoreListLoading) === 1;
	const userPermissionList = useAppSelector(getUserPermissionListSelector);

	const loading = loadingUsersData || storeListLoading;

	const [searchSelected, setSearchSelected] = useState<boolean>(false);
	const [searchInput, setSearchInput] = useState<string | null>(null);
	const [selectedValue, setSelectedValue] = useState<string | null>(null);

	const [islocked, setLocked] = useState(false);

	const [mode, setMode] = useState<string>(ViewMode.READ);

	const [adminData, setAdminData] = useState<AdminData>({
		username: "",
		email: ""
	});
	const [tenantCode, setTenantCode] = useState<string>();

	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
		username: false,
		email: false
	});

	const storesDataList: Array<OutletItem> = useMemo(() => {
		if (storeConfigData && storeConfigData?.length) {
			return storeConfigData;
		}
		return [];
	}, [storeConfigData]);

	const selectedUsersDetails = useMemo(() => {
		if (userDetails?.data?.user_list) {
			return userDetails?.data?.user_list[0];
		}
		return {};
	}, [userDetails]);

	const initialValues = selectedUsersDetails;
	const isUserActive = selectedUsersDetails?.active === USER_STATE.enabled || selectedUsersDetails?.active === "1";

	const handleMode = useCallback(
		(value: string) => {
			setMode(value);
		},
		[setMode]
	);

	useEffect(() => {
		if (!state?.username) {
			navigate(Routes.ROLE_MANAGEMENT);
		}
		if (state?.username && pathname?.includes(Routes.EDIT_ACCOUNT)) {
			dispatch(getUserDetails({ username: state?.username }));
		}
		dispatch(getStoreList({}));
		return () => {
			dispatch(resetSelectedAdminDetails());
			dispatch(resetActivateUser());
			dispatch(resetDeactivateUser());
			dispatch(resetEditUser());
			dispatch(resetResendTemporaryPassword());
		};
	}, []);

	const handleAdminDataChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...adminData,
				[id]: value
			};
			setAdminData(newData);
		},
		[adminData, setAdminData]
	);

	const isDetailsUpdated = useMemo(() => {
		const errs = Object.values(fieldErrors)?.filter((v) => !!v);

		if (errs?.length) {
			return false;
		}

		const values = Object.values(adminData).filter((v) => !!v);

		if (values?.length || searchInput) {
			return true;
		}

		return false;
	}, [adminData, initialValues, fieldErrors, searchInput, handleAdminDataChange]);

	const validateFields = useCallback(
		(submit: boolean) => {
			const fieldsToValidate = [
				{ key: "username", validator: validateUsername, required: true },
				{ key: "email", validator: validateEmail, required: true }
			];

			setFieldErrors({
				username: false,
				email: false
			});

			const errorsValidateFields: Array<{ field: string | undefined }> = [];

			fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean; required: boolean }) => {
				const key = field.key as keyof AdminData;
				const value = adminData[key];
				if (value || (submit && field?.required)) {
					const isValid = field.validator(value);

					if (!isValid) {
						errorsValidateFields.push({ field: field.key });
					}
				}
			});

			if (errorsValidateFields.length) {
				const errs: FieldErrors = {};
				errorsValidateFields.forEach((err) => {
					errs[err.field as keyof FieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[adminData, setFieldErrors, validateUsername, validateEmail]
	);

	const onChange = (value: string) => {
		if (value) {
			setSelectedValue(value);
			const selectVal = value.split("*");
			setSearchInput(selectVal[0]);
			setTenantCode(selectVal[2]);
			setSearchSelected(true);
		} else {
			setSelectedValue(null);
			setSearchInput(null);
			setSearchSelected(false);
		}
	};
	const onSearch = (value: string) => {
		if (value) {
			setSearchInput(value);
			setSearchSelected(true);
		} else {
			setSearchInput(null);
			setSearchSelected(false);
		}
	};

	const goBack = useCallback(() => {
		navigate(`${Routes.ACCOUNT_MANAGEMENT}`);
	}, [navigate]);

	const onSuccess = useCallback(() => {
		navigate(Routes.ACCOUNT_MANAGEMENT);
	}, [navigate]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.ACCOUNT_MANAGEMENT}/${Routes.EDIT_ACCOUNT}`);
	}, [navigate]);

	const deactivateUser = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: deactivateError } = await dispatch(deactivateUserAPI(payload)).unwrap();

				if (!deactivateError?.length) {
					CountDownModal({ timer: 3, onTimeComplete: goBack, title: Constants.EDIT_ACCOUNT.ACCOUNT_DEACTIVATED_SUCCESS });
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: `${Routes?.ACCOUNT_MANAGEMENT}/${Routes?.EDIT_ACCOUNT}` } });
				}

				if (error) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						onTimeComplete: goBack,
						title: Constants.EDIT_ACCOUNT.SOMETHING_WENT_WRONG_MESSAGE
					});
				}
			}
		}
	}, [selectedUsersDetails, CountDownModal, goBack]);

	const handleDeactivateUser = useCallback(() => {
		Modal({
			type: "confirm",
			title: Constants.EDIT_ACCOUNT.PROCEED_TO_DEACTIVATE,
			actionButton: {
				label: Constants.EDIT_ACCOUNT.DEACTIVATE_LABEL,
				onClick: deactivateUser
			},
			cancelButton: {
				label: Constants.EDIT_ACCOUNT.GO_BACK,
				onClick: goBack
			}
		});
	}, [Modal, deactivateUser, goBack]);

	const activateUser = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: activateUserError } = await dispatch(activateUserAPI(payload)).unwrap();

				if (!activateUserError?.length) {
					CountDownModal({
						timer: 3,
						onTimeComplete: goBack,
						title: Constants.EDIT_ACCOUNT.ACCOUNT_REACTIVATED_SUCCESS
					});
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: `${Routes?.ACCOUNT_MANAGEMENT}/${Routes?.EDIT_ACCOUNT}` } });
				}

				if (error) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						onTimeComplete: goBack,
						title: Constants.EDIT_ACCOUNT.SOMETHING_WENT_WRONG_MESSAGE
					});
				}
			}
		}
	}, [selectedUsersDetails, activateUserAPI, dispatch, CountDownModal, goBack]);

	const resendPassword = useCallback(async () => {
		if (selectedUsersDetails?.username) {
			const payload = { username: selectedUsersDetails?.username };

			try {
				const { errors: resendPasswordError, message } = await dispatch(resendTemporaryPasswordAPI(payload)).unwrap();

				if (!resendPasswordError?.length) {
					CountDownModal({
						timer: 3,
						onTimeComplete: goBack,
						title: Constants.EDIT_ACCOUNT.ACCOUNT_RESEND_PASSWORD_SUCCESS,
						subtitle: message || Constants.EDIT_ACCOUNT.ACCOUNT_RESEND_PASSWORD_SUCCESS_RESPONSE
					});
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: `${Routes?.ACCOUNT_MANAGEMENT}/${Routes?.EDIT_ACCOUNT}` } });
				}

				if (error) {
					const errorObj = err?.response?.data?.errors;
					const errorMessage = errorObj?.[0]?.message || Constants.EDIT_ACCOUNT.SOMETHING_WENT_WRONG_MESSAGE;

					CountDownModal({
						timer: 3,
						errorIcon: true,
						onTimeComplete: goBack,
						title: errorMessage
					});
				}
			}
		}
	}, [selectedUsersDetails, resendTemporaryPasswordAPI, dispatch, CountDownModal, goBack, navigate]);

	const lockUser = useCallback(() => {
		CountDownModal({
			timer: 3,
			onTimeComplete: () => setLocked(true),
			title: Constants.EDIT_ACCOUNT.ACCOUNT_SUCCESSFULLY_LOCKED
		});
	}, [goBack, CountDownModal, setLocked]);

	const unlockUser = useCallback(() => {
		CountDownModal({
			timer: 3,
			onTimeComplete: goBack,
			title: Constants.EDIT_ACCOUNT.ACCOUNT_SUCCESSFULLY_UNLOCKED
		});
	}, [goBack, CountDownModal]);

	const handleLockUser = useCallback(() => {
		Modal({
			type: "confirm",
			title: Constants.EDIT_ACCOUNT.PROCEED_TO_LOCK,
			actionButton: {
				label: Constants.EDIT_ACCOUNT.LOCK_ACCOUNT_LABEL,
				onClick: lockUser
			},
			cancelButton: {
				label: "Go Back",
				onClick: goBack
			}
		});
	}, [lockUser, goBack, Modal]);

	const handleResendPassword = useCallback(() => {
		Modal({
			closable: true,
			type: "confirm",
			title: Constants.EDIT_ACCOUNT.ACCOUNT_RESEND_PASSWORD_WARNING,
			actionButton: {
				label: Constants.EDIT_ACCOUNT.RESEND_PASSWORD_BTN_LABEL,
				onClick: resendPassword
			},
			cancelButton: {
				label: Constants.EDIT_ACCOUNT.GO_BACK,
				onClick: goBack
			}
		});
	}, [Modal, resendPassword, goBack]);

	const canResendPassword = useMemo(() => {
		if (!userPermissionList || !Array.isArray(userPermissionList)) {
			return false;
		}
		return !!userPermissionList.find(({ perm_code }) => perm_code === "CREATE_EDIT_TENANT_ACCOUNT");
	}, [userPermissionList]);

	const createPayload = useCallback(async () => {
		return {
			username: `${initialValues?.username}`,
			email: `${adminData?.email || initialValues?.email}`,
			tenant_code: `${tenantCode || initialValues?.tenant_code}`,
			groups: initialValues?.groups
		};
	}, [adminData, tenantCode, initialValues]);

	const handleChange = (e: { target: { id: any; value: any } }) => {
		handleAdminDataChange({
			id: e?.target?.id,
			value: e?.target?.value
		});
	};

	const createUserFailure = useCallback(
		(errorObj: Array<{ message: string }>) => {
			if (errorObj?.length > 0) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: errorObj[0]?.message,
					onTimeComplete: onFailure
				});
			}
		},
		[CountDownModal, onFailure]
	);

	const madeChangesHandler = useCallback(async () => {
		const isFieldsValid = isDetailsUpdated && validateFields(false);

		if (isDetailsUpdated && isFieldsValid) {
			const payload: CreateUserPayload = await createPayload();

			try {
				const { errors: editUserError } = await dispatch(editUserAPI(payload)).unwrap();

				if (!editUserError?.length) {
					CountDownModal({
						timer: 3,
						onTimeComplete: goBack,
						title: Constants.EDIT_ACCOUNT.CHANGES_SUCCESSFULLY_MADE
					});
				}
			} catch (error) {
				// Added any as it is axios object
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN, { state: { path: Routes?.ACCOUNT_MANAGEMENT } });
				}

				const errorObj = err?.response?.data?.errors;
				createUserFailure(errorObj);
			}
		}
	}, [isDetailsUpdated, createPayload, goBack, dispatch, editUserAPI, CountDownModal, validateFields]);

	return {
		isDetailsUpdated,
		isUserActive,
		initialValues,
		handleMode,
		handleDeactivateUser,
		deactivateUser,
		activateUser,
		handleLockUser,
		lockUser,
		resendPassword,
		handleResendPassword,
		canResendPassword,
		islocked,
		mode,
		onChange,
		searchInput,
		selectedValue,
		searchSelected,
		storesDataList: uniqBy(storesDataList, "tenant_code"),
		loading: loading && !data && !errors?.length,
		backgroundLoading: loading && data,
		onSearch,
		handleChange,
		madeChangesHandler,
		goBack,
		unlockUser,
		onSuccess,
		adminData,
		fieldErrors,
		validateFields,
		handleAdminDataChange
	};
}
